/**
 * components/dashboard/UserDashboard.tsx
 *
 * Standard user-facing dashboard. Extracted from Dashboard.tsx to allow
 * Dashboard.tsx to do an admin/user branch without violating React hooks rules.
 * Contains all data fetching and rendering for non-admin users.
 * Updated Jun 2026: uses useEffectiveUser so the admin sees the impersonated
 * user's dashboard data when in impersonation mode.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useEffectiveUser } from '../../hooks/useEffectiveUser';
import { supabase } from '../../lib/supabase';
import { captureError } from '../../utils/errorReporting';
import AppLayout from '../layout/AppLayout';
import Spinner from '../ui/Spinner';
import DashboardActivityCard from './DashboardActivityCard';
import DashboardFormsOverview from './DashboardFormsOverview';
import DashboardOnboardingCard from './DashboardOnboardingCard';
import { useUserForms } from '../../hooks/useUserForms';
import type { ToReviewRequest } from './DashboardActivityCard';
import { getGreeting } from '../../utils/stringHelpers';

const TITLE_CLASSNAME = 'font-serif font-medium tracking-tight';

interface DashboardData {
  toReviewCount: number;
  totalClientsCount: number;
  toReviewRequests: ToReviewRequest[];
}

interface RawRequestWithClient {
  id: string;
  title: string;
  completed_at: string | null;
  client_id: string | null;
  clients: { name: string | null; first_name: string | null; last_name: string | null } | null;
}

const EMPTY_DATA: DashboardData = {
  toReviewCount: 0,
  totalClientsCount: 0,
  toReviewRequests: [],
};

export default function UserDashboard() {
  const { userProfile } = useAuth();
  const { effectiveUserId, isImpersonating } = useEffectiveUser();
  const [data, setData] = useState<DashboardData>(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const { forms: recentForms, loading: formsLoading } = useUserForms({ limit: 5 });

  useEffect(() => {
    fetchDashboardData();
  }, [effectiveUserId]);

  const fetchDashboardData = async () => {
    if (!effectiveUserId) return;

    try {
      const [
        { count: toReviewCount, error: toReviewError },
        { count: totalClientsCount, error: totalClientsError },
        { data: requestsData, error: requestsError },
      ] = await Promise.all([
        supabase
          .from('requests')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', effectiveUserId)
          .eq('status', 'completed')
          .is('viewed_at', null),
        supabase
          .from('clients')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', effectiveUserId),
        supabase
          .from('requests')
          .select('id, title, completed_at, client_id, clients(name, first_name, last_name)')
          .eq('user_id', effectiveUserId)
          .eq('status', 'completed')
          .is('viewed_at', null)
          .order('completed_at', { ascending: false })
          .limit(5),
      ]);

      if (toReviewError) { console.error('Error fetching to-review count:', toReviewError); captureError(toReviewError, { page: 'UserDashboard', action: 'fetchToReviewCount', userId: effectiveUserId }); }
      if (totalClientsError) { console.error('Error fetching clients count:', totalClientsError); captureError(totalClientsError, { page: 'UserDashboard', action: 'fetchClientsCount', userId: effectiveUserId }); }
      if (requestsError) { console.error('Error fetching requests:', requestsError); captureError(requestsError, { page: 'UserDashboard', action: 'fetchRequests', userId: effectiveUserId }); }

      const mappedRequests: ToReviewRequest[] = (
        (requestsData as RawRequestWithClient[]) || []
      ).map((req) => ({
        id: req.id,
        title: req.title,
        completed_at: req.completed_at,
        client_id: req.client_id,
        client_name: req.clients?.name ?? null,
        client_first_name: req.clients?.first_name ?? null,
        client_last_name: req.clients?.last_name ?? null,
      }));

      setData({
        toReviewCount: toReviewCount || 0,
        totalClientsCount: totalClientsCount || 0,
        toReviewRequests: mappedRequests,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      captureError(error, { page: 'UserDashboard', action: 'fetchDashboardData', userId: effectiveUserId });
    } finally {
      setLoading(false);
    }
  };

  // Use impersonated user's name for the greeting when applicable
  const greetingFirstName = isImpersonating
    ? undefined
    : userProfile?.firstName;

  const greetingTitle = useMemo(
    () => getGreeting(greetingFirstName).full,
    [greetingFirstName]
  );

  if (loading) {
    return (
      <AppLayout
        breadcrumbs={[{ label: 'Qollect', path: '/dashboard' }, { label: 'Accueil' }]}
        title={greetingTitle}
        titleClassName={TITLE_CLASSNAME}
      >
        <div className="text-center py-16">
          <div className="text-primary-600 flex justify-center mb-4">
            <Spinner size="md" />
          </div>
          <p className="text-neutral-500">Chargement...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      breadcrumbs={[{ label: 'Qollect', path: '/dashboard' }, { label: 'Accueil' }]}
      title={greetingTitle}
      titleClassName={TITLE_CLASSNAME}
    >
      <div className="space-y-6">
        {!isImpersonating && <DashboardOnboardingCard />}
        <DashboardActivityCard
          toReviewCount={data.toReviewCount}
          totalClientsCount={data.totalClientsCount}
          requests={data.toReviewRequests}
          hasMore={data.toReviewCount > data.toReviewRequests.length}
        />
        <DashboardFormsOverview forms={recentForms} loading={formsLoading} />
      </div>
    </AppLayout>
  );
}
